import { Link } from "react-router-dom";
import { Logo, WordMark } from "@/components/Logo";
import { Shield, Zap, Camera, MapPin, FileText, ArrowRight, CheckCircle2 } from "lucide-react";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Top bar */}
      <header className="border-b border-slate-200 sticky top-0 z-40 bg-white/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-24 flex items-center justify-between">
          <Logo className="h-16" />
          <div className="flex items-center gap-2">
            <Link to="/pricing" data-testid="nav-pricing-link"
              className="px-4 py-2 text-sm font-bold text-[#1E293B] hover:text-[#F77F2A] transition">
              Piani
            </Link>
            <Link to="/login" data-testid="nav-login-link"
              className="px-4 py-2 text-sm font-bold text-[#1E293B] hover:text-[#2456C6] transition">
              Accedi
            </Link>
            <Link to="/register-consultant" data-testid="nav-register-link"
              className="hidden sm:inline-flex btn-gs-primary !px-5 !py-2.5 text-sm">
              Diventa consulente
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0F172A]">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, #2456C6 0%, transparent 35%), radial-gradient(circle at 80% 60%, #F77F2A 0%, transparent 40%)"
        }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 border border-white/20 rounded-full text-xs uppercase tracking-[0.2em] text-orange-300 mb-6 font-bold">
              <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse"></span>
              MVP attivo · Italia
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.05] mb-6">
              Hai avuto un incidente? <br />
              <span className="text-[#F77F2A]">Il tuo consulente</span> <br />
              ti assiste in 60 secondi.
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mb-8 leading-relaxed">
              GoStreet collega l'automobilista al proprio consulente infortunistico. Scansiona il QR, invia foto + GPS, ricevi pratica firmata via OTP. Senza stress.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/c/demo" data-testid="hero-try-demo-button" className="btn-gs-primary">
                Prova la demo cliente <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/login" data-testid="hero-login-button" className="bg-white/10 hover:bg-white/15 text-white border border-white/20 font-bold rounded-xl px-6 py-4 inline-flex items-center gap-2 transition">
                Accedi
              </Link>
            </div>
            <div className="mt-3 text-xs text-slate-400 flex items-center gap-1.5">
              <Shield className="w-3 h-3" />
              <span>Login unificato · l&apos;account viene riconosciuto automaticamente dal sistema</span>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              {[
                { v: "< 60s", l: "Per inviare" },
                { v: "eIDAS", l: "Firma valida" },
                { v: "PWA", l: "Installabile" },
              ].map((s, i) => (
                <div key={i}>
                  <div className="text-2xl sm:text-3xl font-black text-white">{s.v}</div>
                  <div className="text-xs uppercase tracking-wider text-slate-400 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="text-center mb-14">
          <div className="text-xs uppercase tracking-[0.2em] text-[#2456C6] font-bold mb-3">Come funziona</div>
          <h2 className="text-3xl sm:text-4xl font-black text-[#1E293B]">Tre passi. Niente moduli infiniti.</h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Camera, title: "1. Il cliente segnala", desc: "Scansiona il QR del consulente, allega foto + GPS, descrive in 2 righe.", color: "#F77F2A" },
            { icon: Zap, title: "2. Il consulente riceve", desc: "Email immediata con codice pratica, dati e link diretto alla dashboard.", color: "#2456C6" },
            { icon: FileText, title: "3. Pratica firmata", desc: "OTP via email + conferma link. Firma elettronica FES eIDAS valida.", color: "#1E293B" },
          ].map((card, i) => (
            <div key={i} className="card-gs hover:-translate-y-1 transition-transform">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: `${card.color}15`, color: card.color }}>
                <card.icon className="w-6 h-6" strokeWidth={2.5} />
              </div>
              <h3 className="text-xl font-bold text-[#1E293B] mb-2">{card.title}</h3>
              <p className="text-slate-600 leading-relaxed">{card.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features for consultants */}
      <section className="bg-[#F8FAFC] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#F77F2A] font-bold mb-3">Per consulenti</div>
              <h2 className="text-3xl sm:text-4xl font-black text-[#1E293B] mb-6 leading-tight">
                Il tuo studio,<br />sul telefono di ogni cliente.
              </h2>
              <ul className="space-y-4">
                {[
                  "QR personale stampato su biglietto, parasole, WhatsApp",
                  "Dashboard pratiche con stato in tempo reale",
                  "Tasti rapidi configurabili (carro attrezzi, carrozzeria…)",
                  "ZIP archivio automatico a chiusura pratica (PDF + foto)",
                  "Branding personalizzato: logo, colori, recapiti",
                  "GDPR e firma FES eIDAS conformi",
                ].map((s, i) => (
                  <li key={i} className="flex gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#22C55E] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{s}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="/register-consultant" data-testid="features-register-cta" className="btn-gs-secondary">
                  Crea il tuo studio gratis <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] rounded-3xl bg-gradient-to-br from-[#2456C6] to-[#0F172A] p-8 shadow-2xl">
                <div className="bg-white rounded-2xl h-full p-5 overflow-hidden">
                  <div className="flex items-center justify-between mb-4">
                    <WordMark className="text-xl" />
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">Pratiche</span>
                  </div>
                  <div className="space-y-2">
                    {[
                      { code: "GS-000124", name: "Luca Verdi", status: "nuova" },
                      { code: "GS-000123", name: "Anna Bianchi", status: "presa_in_carico" },
                      { code: "GS-000122", name: "Marco Russo", status: "in_lavorazione" },
                      { code: "GS-000121", name: "Sara Conti", status: "chiusa" },
                    ].map((p, i) => (
                      <div key={i} className="flex items-center justify-between py-3 px-3 rounded-lg bg-slate-50 border border-slate-100">
                        <div>
                          <div className="font-bold text-sm text-[#1E293B]">{p.code}</div>
                          <div className="text-xs text-slate-500">{p.name}</div>
                        </div>
                        <span className={`badge-status badge-${p.status} !text-[10px] !px-2 !py-0.5`}>
                          {p.status.replace(/_/g, " ")}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Logo className="h-12" />
            <span className="text-xs text-slate-500">© 2026 · Marchio di TotalKlick srls</span>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[#2456C6] transition">Privacy</Link>
            <Link to="/cookie-policy" data-testid="footer-cookie-link" className="hover:text-[#2456C6] transition">Cookie</Link>
            <Link to="/termini" data-testid="footer-terms-link" className="hover:text-[#2456C6] transition">Termini</Link>
            <Link to="/trasparenza-ai" data-testid="footer-ai-link" className="hover:text-[#2456C6] transition">Trasparenza AI</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
